import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Globe, Check } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useUiLanguages } from '@/hooks/useUiLanguages';
import type { Language as AppLanguage } from '@/stores/language.store';
import { clampMenuLeft, MENU_ESTIMATED_WIDTH } from '@/lib/clamp-menu-position';
import {
  applyMarketingLanguageNavigation,
  isMarketingStackRoute,
} from '@/lib/marketing-stack-locale';

interface HeaderLanguageSelectorProps {
  className?: string;
  buttonClassName?: string;
  /** Au-dessus du header sticky (50) et du drawer mobile (210). */
  menuZIndexClass?: string;
}

export function HeaderLanguageSelector({
  className = '',
  buttonClassName = '',
  menuZIndexClass = 'z-[250]',
}: HeaderLanguageSelectorProps) {
  const { language, changeLanguage, t } = useTranslation();
  const uiLanguages = useUiLanguages();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const repositionMenu = useCallback(() => {
    const trigger = containerRef.current?.getBoundingClientRect();
    if (!trigger) return;
    const menuWidth = menuPanelRef.current?.getBoundingClientRect().width ?? MENU_ESTIMATED_WIDTH;
    const menuHeight = menuPanelRef.current?.getBoundingClientRect().height ?? uiLanguages.length * 40 + 16;
    const inDrawer = Boolean(containerRef.current?.closest('.lo-public-header__drawer-lang'));
    const spaceBelow = window.innerHeight - trigger.bottom;
    const openUpward = inDrawer || spaceBelow < menuHeight + 12;

    setMenuPos({
      top: openUpward ? Math.max(8, trigger.top - menuHeight - 8) : trigger.bottom + 8,
      left: clampMenuLeft(trigger, menuWidth, 'end'),
    });
  }, [uiLanguages.length]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    repositionMenu();
    const onScrollOrResize = () => repositionMenu();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, repositionMenu]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const node = event.target as Node;
      if (containerRef.current?.contains(node)) return;
      if (menuPanelRef.current?.contains(node)) return;
      setIsOpen(false);
      setMenuPos(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang: AppLanguage) => {
    setIsOpen(false);
    setMenuPos(null);
    if (isMarketingStackRoute(window.location.pathname)) {
      applyMarketingLanguageNavigation(lang);
      return;
    }
    changeLanguage(lang);
  };

  const openMenu = () => {
    const trigger = containerRef.current?.getBoundingClientRect();
    if (trigger) {
      const estimatedHeight = uiLanguages.length * 40 + 16;
      const inDrawer = Boolean(containerRef.current?.closest('.lo-public-header__drawer-lang'));
      const spaceBelow = window.innerHeight - trigger.bottom;
      const openUpward = inDrawer || spaceBelow < estimatedHeight + 12;
      setMenuPos({
        top: openUpward ? Math.max(8, trigger.top - estimatedHeight - 8) : trigger.bottom + 8,
        left: clampMenuLeft(trigger, MENU_ESTIMATED_WIDTH, 'end'),
      });
    }
    setIsOpen(true);
  };

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <button
        type="button"
        onClick={() => {
          if (isOpen) {
            setIsOpen(false);
            setMenuPos(null);
          } else {
            openMenu();
          }
        }}
        className={`flex items-center justify-center px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 group text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] ${buttonClassName}`}
        aria-label={String(t('publicHeader.language.select', { defaultValue: 'Select language' }))}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe className="h-4 w-4 text-[#475569] group-hover:text-[#2563EB] transition-colors" />
      </button>

      {isOpen && menuPos && createPortal(
        <div
          ref={menuPanelRef}
          role="listbox"
          dir="ltr"
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
          }}
          className={`${menuZIndexClass} w-52 max-w-[calc(100vw-1rem)] rounded-2xl border border-[#E2E8F0] bg-white py-2 shadow-xl backdrop-blur-lg`}
        >
          {uiLanguages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={language === lang.code}
              onClick={() => handleLanguageSelect(lang.code as AppLanguage)}
              className={`flex w-full items-center justify-between px-4 py-2 text-start text-sm transition-colors ${
                language === lang.code
                  ? 'bg-[#DBEAFE] text-[#2563EB]'
                  : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
              }`}
            >
              <span lang={lang.code}>{`${lang.flag} ${lang.label}`}</span>
              {language === lang.code && <Check className="h-4 w-4 shrink-0 text-[#2563EB]" />}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
